///
/// EarthBrowser
///
/// Viewport Bounding Box
///
/// Computes a lon/lat bounding box for the visible portion of the globe
/// analytically. The visible region is the intersection of the sphere with
/// five half-spaces — the horizon cap (points facing the camera) and the
/// four frustum side planes (expanded by a screen-space margin). The lat/lon
/// extrema of that region occur only at closed-form candidate points:
/// per-circle latitude/longitude extrema, pairwise circle intersections,
/// and the poles. Used by viewport-driven dataset layers (city labels,
/// webcams) to scope database queries to what the camera can actually see.
///
/// Frame conventions (matches utils/geographic.ts): y is the polar axis,
/// longitude = atan2(x, z), Earth approximated as a sphere of radius wgs.a.
///
/// Created by Matt Giger on 9/8/2026
/// Copyright (c) 2026 Geodata Labs
///

import { wgs } from "@/earthbrowser/utils/geographic"
import type { RenderState } from "@stovokor/engine/RenderState"
import { cross, dot, normal, vec3 } from "@stovokor/math/vector"

const cDeg = 180 / Math.PI

/** Half-space constraint on unit-sphere points u: dot(u, n) >= c */
interface Constraint {
	n: vec3
	c: number
}

const wrap180 = (x: number): number => ((((x + 180) % 360) + 360) % 360) - 180

const clamp = (x: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, x))

/**
 * Compute the [minLon, minLat, maxLon, maxLat] bbox (degrees) of the visible
 * globe area, with a screen-space margin in pixels. Exact for a spherical
 * Earth: never degrades with camera tilt or altitude. Returns null only when
 * the globe is entirely outside the (margin-expanded) view frustum. When the
 * view crosses the antimeridian, minLon > maxLon.
 */
export function computeViewportBBox(state: RenderState, marginPx: number): [number, number, number, number] | null {
	const p = state.camera.position
	const r = p.length
	if (r <= wgs.a) return null

	// Horizon cap: a surface point x is camera-facing iff dot(x, p) >= a².
	// On unit-sphere points u = x/a this is dot(u, p/r) >= a/r.
	const constraints: Constraint[] = [{ n: normal(p), c: wgs.a / r }]

	// Frustum side planes through the eye, from margin-expanded corner rays.
	// Inward normal m gives dot(x - p, m) >= 0, i.e. dot(u, m) >= dot(p, m)/a.
	const w = state.width
	const h = state.height
	const m = marginPx
	const corners = [
		state.camera.rayFrom(-m, -m).direction,
		state.camera.rayFrom(w + m, -m).direction,
		state.camera.rayFrom(w + m, h + m).direction,
		state.camera.rayFrom(-m, h + m).direction,
	]
	const centerDir = state.camera.rayFrom(w * 0.5, h * 0.5).direction
	for (let i = 0; i < 4; i++) {
		const raw = cross(corners[i], corners[(i + 1) % 4])
		const rawLen = raw.length
		if (rawLen < 1e-12) continue
		let n = raw.multiply(1 / rawLen)
		if (dot(n, centerDir) < 0) n = n.multiply(-1)
		const c = dot(p, n) / wgs.a
		if (c >= 1) return null // this half-space excludes the entire globe
		if (c <= -1) continue // trivially satisfied everywhere; no boundary circle
		constraints.push({ n, c })
	}

	const candidates: vec3[] = []
	const inside = (u: vec3): boolean => {
		for (const { n, c } of constraints) {
			if (dot(u, n) < c - 1e-7) return false
		}
		return true
	}
	const addIfInside = (u: vec3): boolean => {
		if (!inside(u)) return false
		candidates.push(u)
		return true
	}

	// A boundary circle "winds" around the pole axis when longitude is
	// monotonic along it (no longitude extrema exist on the circle). If such
	// a circle contributes any point to the region boundary, the region
	// wraps all longitudes without necessarily containing a pole.
	const winding: boolean[] = []
	let fullLon = false

	// Candidate type 1: per-circle latitude/longitude extrema.
	// Each constraint plane cuts the sphere in the circle
	// u(θ) = c·n + ρ(A cosθ + B sinθ), ρ = sqrt(1 - c²), A,B ⊥ n.
	for (const { n, c } of constraints) {
		const rho = Math.sqrt(Math.max(0, 1 - c * c))
		if (rho < 1e-9) {
			winding.push(false)
			continue
		}
		const seed = Math.abs(n.y) < 0.9 ? vec3.unitY : vec3.unitX
		const axisA = normal(cross(seed, n))
		const axisB = cross(n, axisA) // so that cross(axisA, axisB) = n
		const center = n.multiply(c)
		const pointAt = (theta: number): vec3 =>
			center.add(axisA.multiply(rho * Math.cos(theta))).add(axisB.multiply(rho * Math.sin(theta)))

		// Longitude extrema: d(atan2(x,z))/dθ = 0 reduces to
		// P cosθ + Q sinθ = -ρ·n.y with the P/Q below. No solution means
		// longitude is monotonic around the circle (it winds around the pole).
		const cp = center.z * axisB.x - center.x * axisB.z
		const cq = center.x * axisA.z - center.z * axisA.x
		const amp = Math.hypot(cp, cq)
		const rhs = -rho * n.y
		const winds = !(amp > 1e-12 && Math.abs(rhs) <= amp)
		winding.push(winds)
		if (!winds) {
			const phi = Math.atan2(cq, cp)
			const alpha = Math.acos(clamp(rhs / amp, -1, 1))
			addIfInside(pointAt(phi + alpha))
			addIfInside(pointAt(phi - alpha))
		}

		// Latitude extrema: extremize u.y over the circle
		const thetaLat = Math.atan2(axisB.y, axisA.y)
		const inA = addIfInside(pointAt(thetaLat))
		const inB = addIfInside(pointAt(thetaLat + Math.PI))
		if (winds && (inA || inB)) fullLon = true
	}

	// Candidate type 2: pairwise circle intersections (arc endpoints)
	for (let i = 0; i < constraints.length; i++) {
		for (let j = i + 1; j < constraints.length; j++) {
			const ni = constraints[i].n
			const nj = constraints[j].n
			const k = dot(ni, nj)
			const det = 1 - k * k
			if (det < 1e-12) continue
			const ai = (constraints[i].c - constraints[j].c * k) / det
			const aj = (constraints[j].c - constraints[i].c * k) / det
			const inPlane = ni.multiply(ai).add(nj.multiply(aj))
			const gammaSq = 1 - dot(inPlane, inPlane)
			if (gammaSq < 0) continue
			const axis = cross(ni, nj) // |axis|² = det
			const gamma = Math.sqrt(gammaSq / det)
			const inA = addIfInside(inPlane.add(axis.multiply(gamma)))
			const inB = addIfInside(inPlane.add(axis.multiply(-gamma)))
			if ((inA || inB) && (winding[i] || winding[j])) fullLon = true
		}
	}

	// Candidate type 3: the poles (the only interior critical points of lat/lon)
	const poleLats: number[] = []
	for (const s of [1, -1]) {
		if (inside(new vec3(0, s, 0))) {
			fullLon = true
			poleLats.push(s * 90)
		}
	}

	if (candidates.length === 0 && poleLats.length === 0) return null // globe not in view

	const lats = candidates.map(u => Math.asin(clamp(u.y, -1, 1)) * cDeg).concat(poleLats)
	const minLat = clamp(Math.min(...lats), -85, 85)
	const maxLat = clamp(Math.max(...lats), -85, 85)

	let minLon: number
	let maxLon: number
	if (fullLon || candidates.length === 0) {
		minLon = -180
		maxLon = 180
	} else {
		// Longitudes measured relative to the sub-camera meridian: the region
		// lies inside the horizon cap, so deltas can't wrap unless a winding
		// circle was involved (handled above)
		const lon0 = Math.atan2(p.x, p.z) * cDeg
		let minD = Infinity
		let maxD = -Infinity
		for (const u of candidates) {
			const d = wrap180(Math.atan2(u.x, u.z) * cDeg - lon0)
			if (d < minD) minD = d
			if (d > maxD) maxD = d
		}
		if (maxD - minD > 300) {
			// candidates nearly wrap; use the full range rather than risk a
			// misplaced gap opposite the camera meridian
			minLon = -180
			maxLon = 180
		} else {
			minLon = wrap180(lon0 + minD)
			maxLon = wrap180(lon0 + maxD)
		}
	}

	return [minLon, minLat, maxLon, maxLat]
}
